/**
 * Created by Abner Sui on 07/03/2019.
 * Description:
 * print dialog
 *
 * ------ maintenance history ------
 */

import { Component, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Thread } from '@tamalelibs/models/thread.model';
import { DocumentEntry } from '@tamalelibs/models/document-entry.model';
import { Subscription } from 'rxjs';
import { PrintService } from '../../services/print.service';
import { PrintActions } from './print.model';
import { businessConstants } from '../../constants/business.constants';
import { Router } from '@angular/router';
import { AuthService } from '@tamalelibs/services/auth.service';
import { take } from 'rxjs/operators';
import { AuthHelperService } from '../../services/auth-helper.service';

@Component({
    selector: 'tam-print-dialog',
    templateUrl: './print-dialog.component.html',
    styleUrls: ['./print-dialog.component.scss'],
    standalone: false
})
export class PrintDialogComponent implements OnInit, OnDestroy {

    dialogOpened = false;
    includeSideNotes = false;
    includeAttachments = false;
    isLoading = false;
    periodicHandle;
    printFrame;

    private _threadList: Array<Thread> = [];
    private _attachments: Array<Array<DocumentEntry>> = [];
    private _attachmentsCount = 0;
    private _isFireFox: boolean;
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _renderer2: Renderer2,
        private _printService: PrintService,
        private _router: Router,
        private _authService: AuthService,
        private _authHelper: AuthHelperService,
    ) { }

    ngOnInit() {
        this._isFireFox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        this._destroySubscriptions.push(
            this._printService.dialogSubject$.subscribe(action => {
                if (action.type === PrintActions.OPEN) {
                    this._threadList = action.payload;
                    this._attachments = [];
                    this._attachmentsCount = 0;
                    this._threadList.forEach((thread: Thread) => {
                        const tempArray: Array<DocumentEntry> = [];
                        Array.from(thread.attachments.values()).forEach(attachment => {
                            if (attachment.extension && attachment.extension.toLowerCase() === 'pdf') {
                                tempArray.push(attachment);
                            }
                        });
                        this._attachmentsCount += tempArray.length;
                        this._attachments.push(tempArray);
                    });
                    this.includeSideNotes = false;
                    this.includeAttachments = false;
                    this.isLoading = false;
                    this.dialogOpened = true;
                }
            }),
        );
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    /**
     * Copy from 1.0
     * @param event
     */
    onPreviewClicked(event) {
        this.isLoading = true;
        this._authService.isTokenValid(this._authHelper.getToken()).pipe(
            take(1),
        ).subscribe(res => {
            if (res.toString() === 'true') {
                this.print(this._threadList.map(item => item.id).join(';'), this.includeSideNotes, this.includeAttachments);
            } else {
                this._router.navigate(['/login']);
            }
        });
    }

    onCancelClicked(event) {
        this._reset();
        this._closeDialog();
        if (event) {
            event.stopImmediatePropagation();
        }
    }

    /**
     * Copy from 1.0
     * @param event
     */
    print(ids, includeSideNotes, includeattachments) {
        const strFrameId = businessConstants.common.printPreviewFrameId;
        const url = this._getPrintPreviewUrl(ids, includeSideNotes, includeattachments);
        const frame = this._renderer2.createElement('iframe');
        frame.id = strFrameId;
        this._renderer2.setAttribute(frame, 'src', url);
        this._renderer2.setAttribute(frame, 'name', 'tamele-print-preview-frame');
        this._renderer2.setAttribute(frame, 'style', 'position:absolute;width:1000px;height:1000px;left:-2000px;top:-2000px;');
        this.printFrame = frame;
        this._renderer2.appendChild(document.getElementsByTagName('body').item(0), this.printFrame);
        let printableCount = this._attachmentsCount;
        if (!includeattachments) {
            printableCount = 0;
        }
        // periodic query for note detail load status
        const isIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
        const now = new Date();
        let reloadedForIEOrEdge = false;
        let interval = 300;
        if (isIEOrEdge) {
            interval = 2000;
        }
        const headEle = document.getElementsByTagName('head').item(0);
        if (this._threadList.length > 0 && this._threadList[0].notes[0] && this._threadList[0].notes[0].calculatedSubject) {
            this._renderer2.setProperty(document.getElementsByTagName('title')[0], 'innerText',
                this._threadList[0].notes[0].calculatedSubject);
        }
        this.periodicHandle = setInterval(() => {
            let noteCount = this._threadList.length;
            if (this.includeSideNotes) {
                noteCount = 0;
                this._threadList.forEach(item => {
                    noteCount += item.notes.length;
                });
            }
            if (this._checkLoadStatus(this.printFrame.contentDocument, '.note-detail-render-done', noteCount)
                && this._checkLoadStatus(this.printFrame.contentDocument, 'loadingspinner', 0, false)
                && this._checkLoadStatus(this.printFrame.contentDocument, 'div.attachment-render-done', printableCount)
                && this._allImagesLoadStatus(this.printFrame.contentDocument) && !this._removeFrame(this.printFrame.contentDocument)
                && !this._removeFrame(headEle)) {
                clearInterval(this.periodicHandle);
                if (this._isFireFox) {
                    this.printFrame.contentWindow.print();
                } else {
                    this.printFrame.contentDocument.execCommand('print', false, null);
                }
                this.printFrame.remove();
                this._closeDialog();
            } else if (this.printFrame.contentWindow.location.href.indexOf('/login') > -1) { // check to see session expiration
                clearInterval(this.periodicHandle);
                this.printFrame.remove();
                this._closeDialog();
                this._router.navigate(['/login']);
            } else if (isIEOrEdge) {
                // For IE/Edge, Browser has cache, the 2rd load will be faster
                if (!reloadedForIEOrEdge && new Date().getTime() - now.getTime() > 20000) {
                    reloadedForIEOrEdge = true;
                    this.printFrame.contentWindow.location.reload();
                }
            }
        }, interval);
    }

    /**
     * check the 'complete' status of all images in element.
     *
     * @param {*} element
     * @returns
     * @memberof PrintDialogComponent
     */
    private _allImagesLoadStatus(element) {
        const allImgs = element.querySelectorAll('img');
        for (let i = 0; i < allImgs.length; i++) {
            if (!allImgs[i].complete || allImgs[i].naturalWidth === 0) {
                return false;
            }
        }
        return true;
    }

    /**
     * check the status of all pdf in element.
     *
     * @param {*} element
     * @returns
     * @memberof PrintDialogComponent
     */
    private _checkLoadStatus(element, selector: string, count: number, moreLess: boolean = true) {
        const targets = element.querySelectorAll(selector);
        return moreLess ? (targets.length >= count) : (targets.length <= count);
    }

    private _closeDialog() {
        this.dialogOpened = false;
        this._renderer2.setProperty(document.getElementsByTagName('title')[0], 'innerText', 'Tamale RMS');
    }

    private _getPrintPreviewUrl(ids, includeSideNotes, includeAttachments) {
        return 'printpreview?ids=' + ids + '&includeSideNotes=' + includeSideNotes + '&includeAttachments=' + includeAttachments;
    }

    /**
     * For IE/Edge, need to remove empty iframe element introduced by 3rd party library
     * @param element
     */
    private _removeFrame(element: HTMLElement): boolean {
        const frame = element.querySelectorAll('iframe');
        if (frame && frame.length > 0) {
            for (let i = 0; i < frame.length; i++) {
                this._renderer2.removeChild(element, frame[i]);
            }
        }
        return frame && frame.length > 0;
    }

    private _reset() {
        this._threadList = [];
        if (this.periodicHandle) {
            clearInterval(this.periodicHandle);
        }
        if (this.printFrame) {
            this.printFrame.remove();
        }
        this.includeSideNotes = false;
        this.includeAttachments = false;
        this.isLoading = false;
        this._attachments = [];
        this._attachmentsCount = 0;
    }

}
